import {useCallback, useEffect, useRef, useState} from "react";
import type {ProgressReadoutSnapshot} from "home/components/useProgressReadout";
import type {GameArm} from "./gameArm";
import {transferGamesArm, reportGameEvent} from "./gameAdapters";
import {type GameId, pickGame} from "./gameCatalog";
import {
	createGameReport, decideEtaEligibility, type EndedOutcome, type GameMode, type GameReport, hostedGameEligibility,
	type HostedGameStage, offersPlay, type RunPhase,
} from "./gameSession";

/**
 * React adapters for the transfer-game slot (kitecorp/transferify-ui#2872). They hold state and call
 * `./gameSession`; every rule (eligibility, who sees Play, what is reported and when) lives there.
 */
export interface TransferGame {
	/** Whether to show the Play button. */
	offersPlay: boolean;
	/** The game this transfer picked, once it is eligible; the same for every replay of this transfer. */
	game: GameId | null;
	/** Whether the game is open. */
	open: boolean;
	/** The game was just closed with the ×, so the Play control that replaces it takes focus. */
	returning: boolean;
	play(): void;
	close(): void;
	/** A run ended on its own. */
	runFinished(outcome: EndedOutcome, score: number): void;
	/**
	 * The game was closed, by its × or by leaving the screen, while its run stood at `phase`; what that
	 * reports is `closeOutcome`'s rule, and only the first close of a run reports.
	 */
	runClosed(phase: RunPhase, score: number): void;
	/** The transfer finished: reported once, and only for a transfer that was offered the game. */
	transferCompleted(): void;
	/** `open` for code that runs outside a render, such as the upload's completion. */
	isOpen(): boolean;
}

/**
 * One transfer's game slot. `eligible` is the transfer's eligibility (null while undecided);
 * `transferKey` changes when a new transfer starts, which forgets the previous one's report and game,
 * and closes the game. `random` picks the game (`./gameCatalog`).
 *
 * The game is settled only once the transfer is eligible, inside an effect. The group may already be
 * settled by an earlier read on the same page load, which keeps the one answer (`./gameAdapters`).
 */
export function useTransferGame(
	mode: GameMode, eligible: boolean | null, transferKey: unknown, random: () => number = Math.random,
): TransferGame {
	const [arm, setArm] = useState<GameArm | null>(null);
	const [game, setGame] = useState<GameId | null>(null);
	const [open, setOpenState] = useState(false);
	const [returning, setReturning] = useState(false);
	const openRef = useRef(false);
	const reportRef = useRef<GameReport | null>(null);
	const setOpen = useCallback((value: boolean) => {
		openRef.current = value;
		setOpenState(value);
	}, []);

	useEffect(() => {
		reportRef.current = null;
		setArm(null);
		setGame(null);
		setOpen(false);
		setReturning(false);
	}, [transferKey, setOpen]);

	useEffect(() => {
		if (eligible !== true || reportRef.current !== null) {
			return;
		}
		const assigned = transferGamesArm();
		const picked = pickGame(random, mode);
		reportRef.current = createGameReport(mode, assigned, picked);
		reportGameEvent("GAME_OFFERED", reportRef.current.offered());
		setArm(assigned);
		setGame(picked);
	}, [eligible, mode, transferKey, random]);

	const play = useCallback(() => {
		if (reportRef.current !== null) {
			reportGameEvent("GAME_OPENED", reportRef.current.opened());
		}
		setReturning(false);
		setOpen(true);
	}, [setOpen]);
	const close = useCallback(() => {
		if (openRef.current) setReturning(true);
		setOpen(false);
	}, [setOpen]);
	const runFinished = useCallback((outcome: EndedOutcome, score: number) => {
		if (reportRef.current !== null) {
			reportGameEvent("GAME_RUN_FINISHED", reportRef.current.runFinished(outcome, score));
		}
	}, []);
	const runClosed = useCallback((phase: RunPhase, score: number) => {
		if (reportRef.current !== null) {
			reportGameEvent("GAME_RUN_FINISHED", reportRef.current.closed(phase, score));
		}
	}, []);
	const transferCompleted = useCallback(() => {
		if (reportRef.current !== null) {
			reportGameEvent("GAME_TRANSFER_COMPLETED", reportRef.current.completed());
		}
	}, []);
	const isOpen = useCallback(() => openRef.current, []);

	return {
		offersPlay: arm !== null && game !== null && offersPlay(arm, eligible),
		game, open, returning, play, close, runFinished, runClosed, transferCompleted, isOpen,
	};
}

/**
 * A hosted transfer's slot, from its upload to its success card (`hostedGameEligibility`,
 * kitecorp/transferify-ui#3388). While it is being sent, eligibility is folded over the smoothed readouts, so it
 * is decided by the first ETA sample and never changes after it; the success card offers the game whatever that
 * decided. Each edge between `none` and the rest is a new slot: the rising one starts a transfer, and the
 * falling one forgets it, so the next upload's first render cannot show the previous transfer's game or Play
 * button.
 */
export function useUploadGame(readout: ProgressReadoutSnapshot | null, stage: HostedGameStage): TransferGame {
	const active = stage !== "none";
	const [etaDecision, setEtaDecision] = useState<boolean | null>(null);
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		setEtaDecision(null);
		setAttempt(count => count + 1);
	}, [active]);

	useEffect(() => {
		if (stage === "sending" && readout !== null) {
			setEtaDecision(previous => decideEtaEligibility(previous, readout.etaSeconds));
		}
	}, [readout, stage]);

	return useTransferGame("upload", hostedGameEligibility(stage, etaDecision), attempt);
}
